import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Bar,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Line,
  ComposedChart,
} from "recharts";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * 30-day kcal in vs out trend with net balance bars.
 * Reads `energy_balance_daily` view (BW12 migration). Falls back to a
 * "no data yet" pane when sums are all zero.
 */

type Row = {
  date: string;
  kcal_in: number;
  kcal_out_activity: number;
  kcal_out_bmr: number;
  kcal_out_total: number;
  net_balance_kcal: number;
  target_balance_kcal: number;
};

const RANGES = [
  { key: 7, label: "7d" },
  { key: 14, label: "14d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
] as const;

type RangeKey = (typeof RANGES)[number]["key"];

export function EnergyBalanceChart({ days = 30 }: { days?: number }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [range, setRange] = useState<RangeKey>(
    (RANGES.find((r) => r.key === days)?.key ?? 30) as RangeKey,
  );

  const q = useQuery({
    queryKey: ["energy_balance", userId, range],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - range);
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("energy_balance_daily")
        .select(
          "date, kcal_in, kcal_out_activity, kcal_out_bmr, kcal_out_total, net_balance_kcal, target_balance_kcal",
        )
        .eq("user_id", userId!)
        .gte("date", since.toISOString().slice(0, 10))
        .order("date");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const data = useMemo(() => (q.data ?? []).map((r) => ({
    ...r,
    kcal_in: Number(r.kcal_in),
    kcal_out_activity: Number(r.kcal_out_activity ?? 0),
    kcal_out_bmr: Number(r.kcal_out_bmr ?? 0),
    kcal_out_total: Number(r.kcal_out_total),
    net_balance_kcal: Number(r.net_balance_kcal),
    target_balance_kcal: Number(r.target_balance_kcal ?? 0),
  })), [q.data]);

  if (q.isLoading) return <SkeletonLoader variant="card" height={280} />;
  if (data.length === 0 || data.every((d) => d.kcal_in === 0 && d.kcal_out_total === 0)) {
    return (
      <article
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-5)",
        }}
      >
        <EmptyState
          headline="No energy data yet."
          sub="Once you log meals + sync active-energy from HAE/Strava, this chart populates."
        />
      </article>
    );
  }

  // HOTFIX-BW19.4 PR-A Bug 4a: standardize on a 7-day rolling window for the
  // header avg regardless of the active range so the number stays comparable
  // when the user toggles between 14/30/90d.
  const recent = data.slice(-7);
  const avgNet = recent.length > 0 ? recent.reduce((s, d) => s + d.net_balance_kcal, 0) / recent.length : 0;
  const target = data[data.length - 1]?.target_balance_kcal ?? 0;
  // Net bar coloring per spec: pale red for negative, pale green for
  // positive, gray when within ±50 of target. Surfaces drift at a glance.
  const netColorFor = (net: number): string => {
    if (Math.abs(net - target) <= 50) return "var(--bg-3)";
    if (net < 0) return "color-mix(in oklab, var(--brick) 35%, transparent)";
    return "color-mix(in oklab, var(--severity-ok) 35%, transparent)";
  };

  // HOTFIX-BW19.2 Bug 10: Lifesum-style energy balance breakdown above the
  // chart. Average across the selected range (not just last 7 days) so the
  // numbers stay coherent with the bars below.
  const breakdown = energyBreakdown(data, target);

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>
          Energy balance · {range} days
        </h3>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span className="mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>
            7-day rolling net {avgNet >= 0 ? "+" : ""}{Math.round(avgNet)} kcal · target{" "}
            {target >= 0 ? "+" : ""}
            {target}
          </span>
          <div
            role="tablist"
            aria-label="Time range"
            style={{
              display: "inline-flex",
              gap: 0,
              padding: 2,
              background: "var(--bg-3)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-chip)",
            }}
          >
            {RANGES.map((r) => {
              const active = r.key === range;
              return (
                <button
                  key={r.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRange(r.key)}
                  style={{
                    background: active ? "var(--bg)" : "transparent",
                    color: active ? "var(--fg)" : "var(--fg-3)",
                    border: "none",
                    padding: "4px 10px",
                    fontSize: 11,
                    fontFamily: "var(--font-mono)",
                    cursor: "pointer",
                    borderRadius: "calc(var(--radius-chip) - 2px)",
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>
      <EnergyBreakdownTable {...breakdown} />
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--hair)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={10}
              tickFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              minTickGap={36}
            />
            <YAxis stroke="var(--fg-4)" fontSize={10} width={36} tickLine={false} axisLine={{ stroke: "var(--hair)" }} />
            <Tooltip
              contentStyle={{
                background: "var(--bg-3)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                color: "var(--fg)",
                fontSize: 12,
              }}
              formatter={(v: unknown, name: string) => [`${Math.round(Number(v))} kcal`, name]}
              labelFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { dateStyle: "medium" })}
            />
            <ReferenceLine y={0} stroke="var(--hair-2)" />
            {target !== 0 && (
              <ReferenceLine
                y={target}
                stroke="var(--accent)"
                strokeDasharray="4 4"
                label={{
                  value: `Goal ${target >= 0 ? "+" : ""}${target}`,
                  position: "right",
                  fill: "var(--accent)",
                  fontSize: 10,
                }}
              />
            )}
            <Bar dataKey="net_balance_kcal" name="Net" stroke="var(--accent)">
              {data.map((d) => (
                <Cell key={d.date} fill={netColorFor(d.net_balance_kcal)} />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="kcal_in"
              name="kcal in"
              stroke="var(--severity-ok)"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="kcal_out_total"
              name="kcal out"
              stroke="var(--severity-warn)"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

type Breakdown = {
  consumed: number;
  basal: number;
  active: number;
  expended: number;
  net: number;
  netTone: "ok" | "warn" | "alert";
  rangeLabel: string;
  consumedNote: string | null;
};

/**
 * Per CLAUDE.md adherence-neutral copy rules: this card states what was
 * consumed and expended. It does not say "you ate too much" or "missed your
 * deficit" — colour bands flag drift only when it crosses ±150 / ±300 kcal
 * around the user's target.
 */
function energyBreakdown(data: Row[], target: number): Breakdown {
  if (data.length === 0) {
    return {
      consumed: 0,
      basal: 0,
      active: 0,
      expended: 0,
      net: 0,
      netTone: "ok",
      rangeLabel: "",
      consumedNote: null,
    };
  }
  const sum = (key: keyof Row) =>
    data.reduce((s, d) => s + Number(d[key] ?? 0), 0);
  const n = data.length;
  // HOTFIX-BW19.4 PR-A Bug 4a: Consumed average must skip zero-meal days
  // so a sparse log doesn't look like under-eating. We still expose the
  // raw count so the user can see when the sample is small.
  const daysWithMeals = data.filter((d) => Number(d.kcal_in ?? 0) > 0);
  const mealDayCount = daysWithMeals.length;
  const consumedSum = daysWithMeals.reduce((s, d) => s + Number(d.kcal_in ?? 0), 0);
  const consumed =
    mealDayCount > 0 ? Math.round(consumedSum / mealDayCount) : 0;
  const basal = Math.round(sum("kcal_out_bmr") / n);
  const active = Math.round(sum("kcal_out_activity") / n);
  const expended = basal + active;
  const net = consumed - expended;
  const drift = Math.abs(net - target);
  const netTone: Breakdown["netTone"] =
    drift <= 150 ? "ok" : drift <= 300 ? "warn" : "alert";
  const consumedNote =
    mealDayCount === 0
      ? "No meals logged in range."
      : mealDayCount < n
        ? `Avg of ${mealDayCount} days with logged meals (${mealDayCount} of ${n} in range)`
        : null;
  return {
    consumed,
    basal,
    active,
    expended,
    net,
    netTone,
    rangeLabel: `${n}-day window`,
    consumedNote,
  };
}

function EnergyBreakdownTable(b: Breakdown) {
  const netColor =
    b.netTone === "ok"
      ? "var(--severity-ok)"
      : b.netTone === "warn"
        ? "var(--amber)"
        : "var(--brick)";
  const netSign = b.net >= 0 ? "+" : "";
  return (
    <div
      role="table"
      aria-label="Energy balance breakdown"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        rowGap: 6,
        columnGap: "var(--space-3)",
        padding: "var(--space-3)",
        marginBottom: "var(--space-3)",
        background: "var(--bg-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
      }}
    >
      <span style={{ color: "var(--fg-2)" }}>
        Consumed
        {b.consumedNote && (
          <span
            style={{
              display: "block",
              marginTop: 2,
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
            }}
          >
            {b.consumedNote}
          </span>
        )}
      </span>
      <span className="mono" style={{ color: "var(--fg)", textAlign: "right" }}>
        {b.consumed.toLocaleString()} kcal
      </span>
      <span style={{ color: "var(--fg-2)" }}>
        Expended
        <span style={{ color: "var(--fg-3)", marginLeft: 8, fontSize: "var(--type-meta)" }}>
          basal {b.basal.toLocaleString()} + active {b.active.toLocaleString()}
        </span>
      </span>
      <span className="mono" style={{ color: "var(--fg)", textAlign: "right" }}>
        {b.expended.toLocaleString()} kcal
      </span>
      <span style={{ color: "var(--fg-2)" }}>Net</span>
      <span
        className="mono"
        style={{ color: netColor, textAlign: "right", fontWeight: 500 }}
      >
        {netSign}
        {b.net.toLocaleString()} kcal
      </span>
      {b.rangeLabel && (
        <span
          style={{
            gridColumn: "1 / -1",
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            marginTop: 2,
          }}
        >
          {b.rangeLabel}
        </span>
      )}
    </div>
  );
}
